import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2, AlertCircle, Tag, Save, Check } from 'lucide-react';
import { supabase } from '@/utils/supabase';

// Discounts stored in site_settings as key='discounts', value=[...array]
function generateCode(length = 8) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

const EMPTY_DISCOUNT = {
  code: '',
  type: 'percentage',
  value: '',
  min_order: '',
  usage_limit: '',
  uses: 0,
  expires_at: '',
  active: true,
};

export default function AdminDiscounts() {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [error, setError]         = useState('');
  const [form, setForm]           = useState(EMPTY_DISCOUNT);
  const [showForm, setShowForm]   = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    const { data } = await supabase
      .from('site_settings')
      .select('value')
      .eq('key', 'discounts')
      .single();
    setDiscounts(data?.value || []);
    setLoading(false);
  };

  const persist = async (next) => {
    setSaving(true);
    setError('');
    try {
      const { error: uErr } = await supabase
        .from('site_settings')
        .upsert({ key: 'discounts', value: next }, { onConflict: 'key' });
      if (uErr) throw uErr;
      setDiscounts(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addDiscount = async () => {
    if (!form.code.trim()) { setError('Code is required.'); return; }
    if (!form.value || isNaN(parseFloat(form.value))) { setError('Value is required.'); return; }
    const next = [...discounts, { ...form, id: Date.now().toString(), code: form.code.toUpperCase().trim() }];
    await persist(next);
    setForm(EMPTY_DISCOUNT);
    setShowForm(false);
  };

  const deleteDiscount = async (id) => {
    if (!window.confirm('Delete this discount?')) return;
    await persist(discounts.filter((d) => d.id !== id));
  };

  const toggleActive = async (id) => {
    const next = discounts.map((d) => d.id === id ? { ...d, active: !d.active } : d);
    await persist(next);
  };

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 size={20} className="animate-spin text-[var(--brand)]" />
    </div>
  );

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl text-[var(--ink)]">Discounts</h1>
          <p className="text-[var(--muted)] text-xs font-['Space_Mono'] mt-0.5">{discounts.length} discount code{discounts.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => setShowForm((p) => !p)}
          className="btn-brand px-4 py-2.5 flex items-center gap-2 text-[0.65rem]"
        >
          <Plus size={12} />
          Create Discount
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-400 text-xs font-['Space_Mono'] p-3 bg-red-400/10 border border-red-400/20">
          <AlertCircle size={12} /><span>{error}</span>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <div className="bg-[var(--paper)] border border-[var(--brand)]/40 p-5 space-y-4">
          <h2 className="font-['Space_Mono'] text-[0.62rem] tracking-[0.16em] uppercase text-[var(--brand)]">
            New Discount
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="font-['Space_Mono'] text-[0.58rem] tracking-[0.14em] uppercase text-[var(--muted)]">Code</label>
              <div className="flex gap-2">
                <input
                  value={form.code}
                  onChange={set('code')}
                  placeholder="SAVE20"
                  className="field-surface px-3 py-2 text-sm flex-1 focus:outline-none focus:border-[var(--brand)] uppercase"
                />
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, code: generateCode() }))}
                  className="border border-[var(--line)] px-2 text-[var(--muted)] hover:text-[var(--ink)] transition-colors text-[0.6rem] font-['Space_Mono']"
                >
                  Generate
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="font-['Space_Mono'] text-[0.58rem] tracking-[0.14em] uppercase text-[var(--muted)]">Type</label>
              <select
                value={form.type}
                onChange={set('type')}
                className="field-surface px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand)] font-['Space_Mono']"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount ($)</option>
                <option value="free_shipping">Free Shipping</option>
              </select>
            </div>
            {form.type !== 'free_shipping' && (
              <div className="flex flex-col gap-1.5">
                <label className="font-['Space_Mono'] text-[0.58rem] tracking-[0.14em] uppercase text-[var(--muted)]">
                  {form.type === 'percentage' ? 'Discount (%)' : 'Discount ($)'}
                </label>
                <input
                  type="number"
                  value={form.value}
                  onChange={set('value')}
                  min="0"
                  step={form.type === 'percentage' ? '1' : '0.01'}
                  placeholder="0"
                  className="field-surface px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand)]"
                />
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <label className="font-['Space_Mono'] text-[0.58rem] tracking-[0.14em] uppercase text-[var(--muted)]">Min Order ($)</label>
              <input
                type="number"
                value={form.min_order}
                onChange={set('min_order')}
                min="0"
                step="0.01"
                placeholder="0 = no minimum"
                className="field-surface px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand)]"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="font-['Space_Mono'] text-[0.58rem] tracking-[0.14em] uppercase text-[var(--muted)]">Usage Limit</label>
              <input
                type="number"
                value={form.usage_limit}
                onChange={set('usage_limit')}
                min="0"
                placeholder="0 = unlimited"
                className="field-surface px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand)]"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="font-['Space_Mono'] text-[0.58rem] tracking-[0.14em] uppercase text-[var(--muted)]">Expires</label>
              <input
                type="date"
                value={form.expires_at}
                onChange={set('expires_at')}
                className="field-surface px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand)]"
              />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button
              onClick={addDiscount}
              disabled={saving}
              className="btn-brand px-4 py-2.5 flex items-center gap-2 text-[0.65rem] disabled:opacity-60"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
              Add Discount
            </button>
            <button
              onClick={() => { setShowForm(false); setError(''); }}
              className="border border-[var(--line)] text-[var(--muted)] hover:text-[var(--ink)] px-4 py-2.5 text-[0.65rem] font-['Space_Mono'] tracking-[0.1em] uppercase transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-[var(--paper)] border border-[var(--line)] overflow-hidden">
        {discounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 gap-3">
            <Tag size={28} className="text-[var(--muted)]" strokeWidth={1} />
            <p className="text-[var(--muted)] text-sm font-['Space_Grotesk']">No discount codes yet</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--line)]">
                {['Code', 'Type', 'Value', 'Uses', 'Expires', 'Status', ''].map((h) => (
                  <th key={h} className="text-left px-5 py-3 font-['Space_Mono'] text-[0.58rem] tracking-[0.12em] uppercase text-[var(--muted)]">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--line)]">
              {discounts.map((d) => (
                <tr key={d.id} className="hover:bg-white/3 transition-colors">
                  <td className="px-5 py-3 font-['Space_Mono'] text-[var(--brand)] text-[0.7rem] tracking-[0.1em]">
                    {d.code}
                  </td>
                  <td className="px-5 py-3 text-[var(--muted)] capitalize">{d.type.replace('_', ' ')}</td>
                  <td className="px-5 py-3 font-['Space_Mono'] text-[var(--ink)]">
                    {d.type === 'percentage' ? `${d.value}%` : d.type === 'fixed' ? `$${d.value}` : '—'}
                  </td>
                  <td className="px-5 py-3 text-[var(--muted)]">
                    {d.uses || 0}{d.usage_limit ? ` / ${d.usage_limit}` : ''}
                  </td>
                  <td className="px-5 py-3 text-[var(--muted)]">
                    {d.expires_at ? new Date(d.expires_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-5 py-3">
                    <button onClick={() => toggleActive(d.id)}>
                      <span className={`inline-block px-2 py-0.5 text-[0.58rem] font-['Space_Mono'] tracking-[0.08em] uppercase ${
                        d.active ? 'text-emerald-400 bg-emerald-400/10' : 'text-[var(--muted)] bg-white/5'
                      }`}>
                        {d.active ? 'Active' : 'Inactive'}
                      </span>
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <button
                      onClick={() => deleteDiscount(d.id)}
                      className="text-[var(--muted)] hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
